import { ReactNode, useState } from 'react';
import { ChevronDown, ChevronUp, Plus, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CollapsibleSectionProps {
  title: string;
  defaultOpen?: boolean;
  children: ReactNode;
  className?: string;
  headerClassName?: string;
  actions?: ReactNode;
  variant?: 'default' | 'card';
}

export function CollapsibleSection({
  title,
  defaultOpen = true,
  children,
  className,
  headerClassName,
  actions,
  variant = 'default',
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const ToggleIcon = isOpen ? Minus : Plus;

  if (variant === 'card') {
    return (
      <div className={cn('rounded-lg border bg-card overflow-hidden', className)}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            'flex w-full items-center justify-between bg-primary px-4 py-2 text-left text-primary-foreground',
            headerClassName
          )}
          aria-expanded={isOpen}
        >
          <span className="text-sm font-semibold uppercase tracking-wider">{title}</span>
          <div className="flex items-center gap-2">
            {actions && (
              <div onClick={(e) => e.stopPropagation()}>
                {actions}
              </div>
            )}
            <ToggleIcon className="h-4 w-4" />
          </div>
        </button>
        <div
          className={cn(
            'transition-all duration-200 ease-in-out',
            isOpen ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
          )}
        >
          <div className="p-4">
            {children}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('rounded-lg border bg-card overflow-hidden', className)}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex w-full items-center justify-between px-4 py-3 text-left hover:bg-muted/50 transition-colors',
          headerClassName
        )}
        aria-expanded={isOpen}
      >
        <span className="text-sm font-semibold">{title}</span>
        <div className="flex items-center gap-2">
          {actions && (
            <div onClick={(e) => e.stopPropagation()}>
              {actions}
            </div>
          )}
          {isOpen ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </button>
      <div
        className={cn(
          'transition-all duration-200 ease-in-out',
          isOpen ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
        )}
      >
        <div className="border-t px-4 py-4">
          {children}
        </div>
      </div>
    </div>
  );
}

export default CollapsibleSection;
